import { computed, ref } from 'vue'
import { useRoute, type RouteRecordName, type RouteRecordNormalized, type Router } from 'vue-router'
import type { LayoutComponentMap } from '../types'

let layoutComponentMap: LayoutComponentMap
let defaultLayout: string

const layouts = ref<Record<string, string>>({})

export const useRouteLayout = <T extends LayoutComponentMap = LayoutComponentMap>() => {
  const route = useRoute()

  const activeLayoutComponent = computed(() => {
    const layout = layouts.value[String(route.name)] ?? defaultLayout

    return layoutComponentMap[layout] ?? layoutComponentMap[defaultLayout]
  })

  const populateLayoutsFromRouterConfig = (router: Router) => {
    // Set default layout if route does not have a layout set
    layouts.value = Object.fromEntries(
      router
        .getRoutes()
        .map((route: RouteRecordNormalized): [string, string] => [
          String(route.name),
          String(route.meta.layout ?? defaultLayout)
        ])
    )
  }

  const registerLayouts = (
    layoutMap: T,
    options: { router: Router; defaultLayout: keyof T & string }
  ) => {
    const { router, defaultLayout: defaultLayoutName } = options

    layoutComponentMap = layoutMap
    defaultLayout = defaultLayoutName
    populateLayoutsFromRouterConfig(router)
  }

  const setLayout = (routeName: RouteRecordName, layoutName: keyof T & string) => {
    layouts.value = {
      ...layouts.value,
      [String(routeName)]: layoutName
    }
  }

  return {
    activeLayoutComponent,
    registerLayouts,
    setLayout
  }
}
